import React, { useEffect, useMemo, useState } from "react";
import { api, formatMXN } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, Search, Save, Tags } from "lucide-react";
import { toast } from "sonner";

const emptyForm = { nombre: "", categoria_material: "TRIPLAY", unidad: "HOJA", cantidad: 0, precio_unitario: 0 };

export default function Materiales() {
  const [items, setItems] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [filterCat, setFilterCat] = useState("Todas");
  const [search, setSearch] = useState("");
  const [edits, setEdits] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [catDialog, setCatDialog] = useState(false);
  const [newCatName, setNewCatName] = useState("");

  const CATEGORIAS_MAT = useMemo(() => categorias.map((c) => c.nombre), [categorias]);

  const load = async () => {
    const [m, c] = await Promise.all([api.get("/materiales"), api.get("/material-categorias")]);
    setItems(m.data);
    setCategorias(c.data);
    setEdits({});
  };
  useEffect(() => { load(); }, []);

  const addCategoria = async () => {
    const name = newCatName.trim().toUpperCase();
    if (!name) { toast.error("Nombre vacío"); return; }
    try {
      await api.post("/material-categorias", { nombre: name });
      toast.success(`Categoría "${name}" creada`);
      setNewCatName("");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error al crear";
      toast.error(detail);
    }
  };

  const removeCategoria = async (cat) => {
    if (!window.confirm(`¿Eliminar la categoría "${cat.nombre}"?`)) return;
    try {
      await api.delete(`/material-categorias/${cat.id}`);
      toast.success("Categoría eliminada");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "Error al eliminar";
      toast.error(detail);
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return items.filter((m) => {
      if (filterCat !== "Todas" && m.categoria_material !== filterCat) return false;
      if (q && !m.nombre.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [items, filterCat, search]);

  const categoriasPresentes = useMemo(() => {
    const set = new Set(items.map((m) => m.categoria_material));
    return Array.from(set).sort();
  }, [items]);

  const setField = (id, field, value) => {
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
  };

  const getValue = (m, field) => {
    if (edits[m.id] && edits[m.id][field] !== undefined) return edits[m.id][field];
    return m[field];
  };

  const isDirty = (id) => edits[id] && Object.keys(edits[id]).length > 0;

  const saveRow = async (m) => {
    const changes = edits[m.id];
    if (!changes) return;
    const payload = {};
    for (const k of Object.keys(changes)) {
      if (k === "cantidad" || k === "precio_unitario") payload[k] = Number(changes[k]) || 0;
      else payload[k] = changes[k];
    }
    try {
      await api.put(`/materiales/${m.id}`, payload);
      toast.success(`${m.nombre} actualizado`);
      load();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const saveAll = async () => {
    const ids = Object.keys(edits);
    if (ids.length === 0) { toast.info("Sin cambios"); return; }
    let ok = 0;
    let failures = [];
    for (const id of ids) {
      const m = items.find((x) => x.id === id);
      if (!m) continue;
      const changes = edits[id];
      const payload = {};
      for (const k of Object.keys(changes)) {
        if (k === "cantidad" || k === "precio_unitario") payload[k] = Number(changes[k]) || 0;
        else payload[k] = changes[k];
      }
      try {
        await api.put(`/materiales/${id}`, payload);
        ok++;
      } catch (e) {
        console.error(`saveAll: error en ${m.nombre}`, e);
        failures.push(m.nombre);
      }
    }
    if (failures.length > 0) toast.error(`Fallaron ${failures.length}: ${failures.slice(0,3).join(", ")}…`);
    toast.success(`${ok}/${ids.length} actualizados`);
    load();
  };

  const remove = async (id, nombre) => {
    if (!window.confirm(`¿Eliminar ${nombre}?`)) return;
    await api.delete(`/materiales/${id}`);
    toast.success("Eliminado");
    load();
  };

  const createNew = async () => {
    if (!form.nombre) { toast.error("El nombre es obligatorio"); return; }
    try {
      const payload = {
        ...form,
        cantidad: Number(form.cantidad) || 0,
        precio_unitario: Number(form.precio_unitario) || 0,
      };
      await api.post("/materiales", payload);
      toast.success("Material creado");
      setOpen(false);
      setForm(emptyForm);
      load();
    } catch {
      toast.error("Error al crear");
    }
  };

  const dirtyCount = Object.keys(edits).length;

  return (
    <div className="space-y-8" data-testid="materiales-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="uppercase-label">Inventario maestro</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">Materiales</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Listado de todos los materiales disponibles. Edita el costo, cantidad y unidad
            directamente desde la tabla. Cambios se aplican al guardar.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setCatDialog(true)} data-testid="mat-manage-categorias">
            <Tags className="w-4 h-4 mr-2" /> Categorías ({categorias.length})
          </Button>
          {dirtyCount > 0 && (
            <Button onClick={saveAll} className="bg-[#2C2A28] hover:bg-[#1c1b1a] text-white" data-testid="mat-save-all">
              <Save className="w-4 h-4 mr-2" /> Guardar {dirtyCount}
            </Button>
          )}
          <Button onClick={() => { setForm(emptyForm); setOpen(true); }} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="mat-new">
            <Plus className="w-4 h-4 mr-2" /> Nuevo material
          </Button>
        </div>
      </header>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input data-testid="mat-search" placeholder="Buscar por nombre…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger className="w-full sm:w-56 bg-white" data-testid="mat-filter-cat"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Todas">Todas las categorías</SelectItem>
            {categoriasPresentes.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="border border-border bg-white rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F2EC]/70 text-left sticky top-0">
            <tr>
              <th className="px-3 py-3 uppercase-label">Material</th>
              <th className="px-3 py-3 uppercase-label w-36">Categoría</th>
              <th className="px-3 py-3 uppercase-label w-24">Unidad</th>
              <th className="px-3 py-3 uppercase-label w-24 text-right">Cantidad</th>
              <th className="px-3 py-3 uppercase-label w-36 text-right">Precio unitario</th>
              <th className="px-3 py-3 uppercase-label w-32 text-right">Total</th>
              <th className="px-3 py-3 w-20"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-6 py-10 text-center text-muted-foreground">Sin materiales.</td></tr>
            )}
            {filtered.map((m) => {
              const cantidad = Number(getValue(m, "cantidad")) || 0;
              const precio = Number(getValue(m, "precio_unitario")) || 0;
              const total = cantidad * precio;
              const dirty = isDirty(m.id);
              return (
                <tr key={m.id} className={`border-t border-border ${dirty ? "bg-[#8B5A33]/5" : ""}`} data-testid={`mat-row-${m.id}`}>
                  <td className="px-3 py-2">
                    <Input value={getValue(m, "nombre")} onChange={(e) => setField(m.id, "nombre", e.target.value)} className="h-9 border-transparent hover:border-border focus:border-[#8B5A33] bg-transparent" data-testid={`mat-nombre-${m.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Select value={getValue(m, "categoria_material")} onValueChange={(v) => setField(m.id, "categoria_material", v)}>
                      <SelectTrigger className="h-9" data-testid={`mat-cat-${m.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{CATEGORIAS_MAT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2">
                    <Input value={getValue(m, "unidad")} onChange={(e) => setField(m.id, "unidad", e.target.value)} className="h-9" data-testid={`mat-unidad-${m.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" step="0.01" value={getValue(m, "cantidad")} onChange={(e) => setField(m.id, "cantidad", e.target.value)} className="h-9 text-right tabular" data-testid={`mat-cantidad-${m.id}`} />
                  </td>
                  <td className="px-3 py-2">
                    <Input type="number" step="0.01" value={getValue(m, "precio_unitario")} onChange={(e) => setField(m.id, "precio_unitario", e.target.value)} className="h-9 text-right tabular" data-testid={`mat-precio-${m.id}`} />
                  </td>
                  <td className="px-3 py-2 text-right tabular text-sm text-muted-foreground" data-testid={`mat-total-${m.id}`}>{formatMXN(total)}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-1 justify-end">
                      {dirty && (
                        <Button size="icon" variant="ghost" onClick={() => saveRow(m)} title="Guardar" data-testid={`mat-save-${m.id}`}>
                          <Save className="w-4 h-4 text-[#4A7C59]" />
                        </Button>
                      )}
                      <Button size="icon" variant="ghost" onClick={() => remove(m.id, m.nombre)} title="Eliminar" data-testid={`mat-delete-${m.id}`}>
                        <Trash2 className="w-4 h-4 text-[#BC4749]" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-white" data-testid="mat-form-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl">Nuevo material</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div className="space-y-1.5"><Label>Nombre</Label><Input data-testid="mat-form-nombre" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} placeholder="TZALAM 18 mm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Categoría</Label>
                <Select value={form.categoria_material} onValueChange={(v) => setForm({ ...form, categoria_material: v })}>
                  <SelectTrigger data-testid="mat-form-cat"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIAS_MAT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Unidad</Label><Input data-testid="mat-form-unidad" value={form.unidad} onChange={(e) => setForm({ ...form, unidad: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Cantidad</Label><Input data-testid="mat-form-cantidad" type="number" step="0.01" value={form.cantidad} onChange={(e) => setForm({ ...form, cantidad: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Precio unitario (MXN)</Label><Input data-testid="mat-form-precio" type="number" step="0.01" value={form.precio_unitario} onChange={(e) => setForm({ ...form, precio_unitario: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={createNew} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="mat-form-save">Crear</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={catDialog} onOpenChange={setCatDialog}>
        <DialogContent className="max-w-lg bg-white" data-testid="cat-manage-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Gestionar categorías</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <Input
                data-testid="cat-new-input"
                placeholder="Nueva categoría (ej. CHAPAS)"
                value={newCatName}
                onChange={(e) => setNewCatName(e.target.value.toUpperCase())}
                onKeyDown={(e) => { if (e.key === "Enter") addCategoria(); }}
              />
              <Button onClick={addCategoria} className="bg-[#8B5A33] hover:bg-[#6D4525] text-white" data-testid="cat-add">
                <Plus className="w-4 h-4 mr-1" /> Agregar
              </Button>
            </div>
            <div className="border border-border rounded max-h-80 overflow-y-auto">
              {categorias.length === 0 && (
                <div className="p-6 text-center text-muted-foreground text-sm">Sin categorías.</div>
              )}
              {categorias.map((c) => {
                const usedBy = items.filter((m) => m.categoria_material === c.nombre).length;
                return (
                  <div key={c.id} className="flex items-center justify-between px-4 py-2 border-b border-border last:border-b-0" data-testid={`cat-row-${c.nombre}`}>
                    <div>
                      <div className="font-medium text-sm">{c.nombre}</div>
                      <div className="text-xs text-muted-foreground">{usedBy} material{usedBy === 1 ? "" : "es"}</div>
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => removeCategoria(c)}
                      title={usedBy > 0 ? "En uso, no se puede eliminar" : "Eliminar"}
                      disabled={usedBy > 0}
                      data-testid={`cat-delete-${c.nombre}`}
                    >
                      <Trash2 className={`w-4 h-4 ${usedBy > 0 ? "text-muted-foreground" : "text-[#BC4749]"}`} />
                    </Button>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">Solo puedes eliminar categorías que no estén en uso.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
